<template>
  <div class="container mt-5">
      <div v-if="boardGame" class="board-game-details">
      <div class="row">
        <div class="col-12">
          <div class="mb-4 game-header-image-container">
            <img 
              :src="boardGame.headerImageUrl || getGameImage(boardGame.type)" 
              class="card-img-top" 
              :alt="`${boardGame.name} board game`"
              style="max-height: 300px; object-fit: cover;"
            >
          </div>
        </div>
      </div>
    </div>
    <h1 class="text-left mb-4">{{ editMode ? 'Edit board' : 'New board' }}</h1>
    <div v-if="loadError" class="alert alert-warning">{{ loadError }}</div>
    
    <!-- Alert for view-only mode -->
    <div v-if="viewOnlyMode" class="alert alert-info mb-4">
      <i class="bi bi-info-circle me-2"></i>
      You are in view-only mode. You can view boards but cannot create or edit them.
    </div>
    <!-- The list of boards lives at /build-boards/:gameId (BoardListPage).
         This page edits ONE board, reached from there, so it no longer
         carries a copy of the list. -->
    <BoardCrumbs
      :game-name="boardGameName"
      :game-id="boardGameId"
      :board-name="boardName"
      :board-id="currentBoardId"
      :current="editMode ? 'Edit' : 'New board'"
    />

    <div class="row">
      <div class="col-md-4" v-if="!viewOnlyMode">
        <div class="card mb-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span>{{ editMode ? 'Update Board' : 'Board Configuration' }}</span>
            <button 
              v-if="editMode" 
              @click="cancelEdit" 
              class="btn btn-sm btn-outline-secondary"
            >
              Cancel
            </button>
          </div>
          <div class="card-body">
            <div class="form-group mb-3">
              <label>Board Name</label>
              <input 
                type="text" 
                v-model="boardName" 
                class="form-control" 
                placeholder="Enter board name"
              />
            </div>

            <div class="form-group mb-3">
              <label>Board Description (Optional)</label>
              <textarea 
                v-model="boardDescription" 
                class="form-control" 
                placeholder="Enter board description"
                rows="3"
              ></textarea>
            </div>

            <div class="form-group mb-3">
              <label>Board Version</label>
              <input 
                type="text" 
                v-model="boardVersion" 
                class="form-control" 
                placeholder="Enter board version (e.g., 1.1.1)"
                pattern="^\d+(\.\d+)*$"
              />
            </div>

            <div class="form-group mb-3">
              <label>Board Image</label>
              <input 
                type="file" 
                @change="handleImageUpload" 
                class="form-control" 
                accept="image/*"
              />
              <div v-if="boardImagePreview" class="mt-2">
                <img :src="boardImagePreview" alt="Board preview" class="img-fluid" style="max-height: 150px;" />
              </div>
            </div>

            <div class="form-group mb-3">
              <label>Board Type</label>
              <select 
                v-model="boardType" 
                class="form-control"
              >
                <option value="grid">Grid Board</option>
                <option value="hexagonal">Hexagonal Board</option>
                <option value="circular">Circular Board</option>
                <option value="image">Board Type Image</option>
              </select>
            </div>
            <!-- Add this right after the Board Type selection in the card-body -->

            <div class="form-group mb-3">
              <label>Theme/Visual Style</label>
              <select 
                v-model="boardTheme" 
                class="form-control"
              >
                <option value="fantasy">Fantasy</option>
                <option value="scifi">Sci-Fi</option>
                <option value="medieval">Medieval</option>
                <option value="modern">Modern</option>
                <option value="abstract">Abstract</option>
                <option value="natural">Natural/Organic</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div class="form-group mb-3">
              <label>Color Scheme</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div 
                  v-for="(scheme, index) in colorSchemes" 
                  :key="index"
                  class="color-scheme-option"
                  :class="{ 'active': selectedColorScheme === scheme.name }"
                  @click="selectColorScheme(scheme.name)"
                >
                  <div class="color-preview d-flex">
                    <div 
                      v-for="(color, colorIndex) in scheme.colors" 
                      :key="colorIndex"
                      class="color-sample"
                      :style="{ backgroundColor: color }"
                    ></div>
                  </div>
                  <div class="color-scheme-name">{{ scheme.name }}</div>
                </div>
              </div>
            </div>

            <div class="form-group mb-3" style="display:none;">
              <label>Texture/Pattern</label>
              <div class="d-flex flex-wrap gap-2 mt-2">
                <div 
                  v-for="(pattern, index) in patterns" 
                  :key="index"
                  class="pattern-option"
                  :class="{ 'active': selectedPattern === pattern.name }"
                  @click="selectPattern(pattern.name)"
                >
                  <div 
                    class="pattern-preview" 
                    :style="{ backgroundImage: `url(${pattern.preview})` }"
                  ></div>
                  <div class="pattern-name">{{ pattern.name }}</div>
                </div>
              </div>
            </div>

            <div class="form-group mb-3" style="display:none;" >
              <label>Special Features</label>
              <div class="form-check mb-2">
                <input 
                  type="checkbox" 
                  v-model="specialFeatures.centerZone" 
                  class="form-check-input" 
                  id="centerZoneCheck"
                />
                <label class="form-check-label" for="centerZoneCheck">
                  Include center "special zone"
                </label>
              </div>
              <div class="form-check mb-2">
                <input 
                  type="checkbox" 
                  v-model="specialFeatures.shortcuts" 
                  class="form-check-input" 
                  id="shortcutsCheck"
                />
                <label class="form-check-label" for="shortcutsCheck">
                  Add shortcut paths
                </label>
              </div>
              <div class="form-check mb-2">
                <input 
                  type="checkbox" 
                  v-model="specialFeatures.resources" 
                  class="form-check-input" 
                  id="resourcesCheck"
                />
                <label class="form-check-label" for="resourcesCheck">
                  Include resource collection areas
                </label>
              </div>
              <div class="form-check mb-2" style="display:none;">
                <input 
                  type="checkbox" 
                  v-model="specialFeatures.obstacles" 
                  class="form-check-input" 
                  id="obstaclesCheck"
                />
                <label class="form-check-label" for="obstaclesCheck">
                  Add random obstacles
                </label>
              </div>
            </div>
            <div class="form-group mb-3">
  <label>Print Layout</label>
  <select 
    v-model="printLayout" 
    class="form-control"
  >
    <option value="1">Single Page</option>
    <option value="4">4 Pages (2x2)</option>
    <option value="6">6 Pages (2x3)</option>
  </select>
  <div class="form-check mt-2">
    <input 
      type="checkbox" 
      v-model="showPageIndicators" 
      class="form-check-input" 
      id="pageIndicatorsCheck"
    />
    <label class="form-check-label" for="pageIndicatorsCheck">
      Show page indicators in preview
    </label>
  </div>
</div>

            <div class="form-group mb-3">
              <label>Board Size</label>
              <div class="input-group">
                <input 
                  type="number" 
                  v-model.number="boardSize" 
                  class="form-control" 
                  min="3" 
                  max="20"
                />
                <span class="input-group-text">x</span>
                <input 
                  type="number" 
                  v-model.number="boardHeight" 
                  class="form-control" 
                  min="3" 
                  max="20"
                />
              </div>
            </div>

            <div class="form-group mb-3" style="display:none;">
              <label>Cell Color</label>
              <div class="d-flex align-items-center">
                <input 
                  type="color" 
                  v-model="cellColor" 
                  class="form-control form-control-color me-2"
                  :disabled="transparentCells"
                />
                <div class="form-check">
                  <input 
                    type="checkbox" 
                    v-model="transparentCells" 
                    class="form-check-input" 
                    id="transparentCheck"
                  />
                  <label class="form-check-label" for="transparentCheck">
                    Transparent
                  </label>
                </div>
              </div>
            </div>

            <div class="form-check mb-3" style="display:none;">
              <input 
                type="checkbox" 
                v-model="addRandomObstacles" 
                class="form-check-input" 
                id="obstaclesCheck"
              />
              <label class="form-check-label" for="obstaclesCheck">
                Add Random Obstacles
              </label>
            </div>

            <div class="d-grid gap-2">
             <!--  <button 
                @click="generateBoard" 
                class="btn btn-primary"
              >
                Generate Board
              </button> -->
              <button 
                @click="saveBoard" 
                class="btn btn-success"
                :disabled="!boardName"
              >
                {{ editMode ? 'Update Board' : 'Save Board' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div :class="viewOnlyMode ? 'col-md-12' : 'col-md-8'">
        <div class="row">
          <div class="col-12" v-if="selectedBoard || !viewOnlyMode">
            <div class="card mb-4">
              <div class="card-header d-flex justify-content-between align-items-center">
                <span>
                  {{ selectedBoard ? selectedBoard.name + ' Preview' : 'Board Preview' }}
                  <span class="badge bg-secondary ms-2" v-if="selectedBoard && selectedBoard.version">v{{ selectedBoard.version }}</span>
                </span>
                <div>
                  <button 
                    v-if="selectedBoard"
                    @click="toggleFullScreen" 
                    class="btn btn-sm btn-outline-primary me-2"
                  >
                    <i class="bi" :class="isFullScreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'"></i>
                    {{ isFullScreen ? 'Exit Full Screen' : 'Full Screen' }}
                  </button>
                  <button 
                    v-if="selectedBoard" 
                    @click="closeSelectedBoard" 
                    class="btn btn-sm btn-outline-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div class="card-body">
                
                <!-- <div 
                  ref="boardContainer" 
                  class="board-container"
                  :class="{ 'fullscreen': isFullScreen }"
                  :style="[boardContainerStyle, boardBackgroundStyle]"
                >
                  <template v-if="(selectedBoard ? selectedBoard.type : boardType) === 'grid'">
                    <GridBoard 
                      :width="selectedBoard ? selectedBoard.size : boardSize" 
                      :height="selectedBoard ? (selectedBoard.height || selectedBoard.size) : boardHeight"
                      :cellColor="selectedBoard 
                        ? (selectedBoard.transparentCells ? 'transparent' : selectedBoard.cellColor) 
                        : (transparentCells ? 'transparent' : cellColor)"
                      :obstacles="selectedBoard ? (selectedBoard.obstacles || []) : obstacles"
                    />
                  </template>
                  <template v-else-if="(selectedBoard ? selectedBoard.type : boardType) === 'hexagonal'">
                    <HexagonalBoard 
                      :width="selectedBoard ? selectedBoard.size : boardSize" 
                      :height="selectedBoard ? (selectedBoard.height || selectedBoard.size) : boardHeight"
                      :cellColor="selectedBoard 
                        ? (selectedBoard.transparentCells ? 'transparent' : selectedBoard.cellColor) 
                        : (transparentCells ? 'transparent' : cellColor)"
                      :obstacles="selectedBoard ? (selectedBoard.obstacles || []) : obstacles"
                    />
                  </template>
                  <template v-else-if="(selectedBoard ? selectedBoard.type : boardType) === 'circular'">
                    <CircularBoard 
                      :width="selectedBoard ? selectedBoard.size : boardSize" 
                      :cellColor="selectedBoard 
                        ? (selectedBoard.transparentCells ? 'transparent' : selectedBoard.cellColor) 
                        : (transparentCells ? 'transparent' : cellColor)"
                      :obstacles="selectedBoard ? (selectedBoard.obstacles || []) : obstacles"
                    />
                  </template>
                  <template v-else-if="(selectedBoard ? selectedBoard.type : boardType) === 'image'">
                    <div class="board-image-only">
                      <div v-if="!(selectedBoard ? selectedBoard.imageUrl : boardImagePreview)" class="no-image-placeholder">
                        <p>Please upload a board image</p>
                      </div>
                      <div v-else class="board-image-display">
                        <img :src="selectedBoard ? selectedBoard.imageUrl : boardImagePreview" alt="Board image" class="img-fluid" />
                      </div>
                    </div>
                  </template>
                </div>
                 -->
                 <!-- Update the board-container div in your template to include page indicators -->

<div 
  ref="boardContainer" 
  class="board-container"
  :class="{ 'fullscreen': isFullScreen }"
  :style="[boardContainerStyle, boardBackgroundStyle]"
  :data-print-layout="selectedBoard ? (selectedBoard.printLayout || '1') : printLayout"
>
  <!-- Add page indicators -->
<template v-if="showPageIndicators && (selectedBoard ? (selectedBoard.printLayout || '1') : printLayout) !== '1'">
  <!-- Horizontal lines -->
  <div 
    v-for="(indicator, index) in pageIndicators.filter(i => i.type === 'horizontal')"
    :key="`h-${index}`"
    class="page-indicator horizontal"
    :style="{ top: indicator.position }"
  ></div>
  
  <!-- Vertical lines -->
  <div 
    v-for="(indicator, index) in pageIndicators.filter(i => i.type === 'vertical')"
    :key="`v-${index}`"
    class="page-indicator vertical"
    :style="{ left: indicator.position }"
  ></div>
  
  <!-- Page numbers for 2x2 layout (4 pages) -->
  <template v-if="(selectedBoard ? (selectedBoard.printLayout || '1') : printLayout) === '4'">
    <div class="page-number page-1">1</div>
    <div class="page-number page-2">2</div>
    <div class="page-number page-3">3</div>
    <div class="page-number page-4">4</div>
  </template>
  
  <!-- Page numbers for 2x3 layout (6 pages) -->
  <template v-if="(selectedBoard ? (selectedBoard.printLayout || '1') : printLayout) === '6'">
    <div class="page-number page-1">1</div>
    <div class="page-number page-2">2</div>
    <div class="page-number page-3">3</div>
    <div class="page-number page-4">4</div>
    <div class="page-number page-5">5</div>
    <div class="page-number page-6">6</div>
  </template>
</template>
  
  <!-- Print button -->
<!--   <button 
    class="print-button"
    @click="printBoard"
    title="Print this board"
  >
    <i class="bi bi-printer"></i>
    Print
  </button> -->
  
  <!-- Existing board components -->
  <template v-if="(selectedBoard ? selectedBoard.type : boardType) === 'grid'">
    <GridBoard 
      :width="selectedBoard ? selectedBoard.size : boardSize" 
      :height="selectedBoard ? (selectedBoard.height || selectedBoard.size) : boardHeight"
      :cellColor="selectedBoard 
        ? (selectedBoard.transparentCells ? 'transparent' : selectedBoard.cellColor) 
        : (transparentCells ? 'transparent' : cellColor)"
      :obstacles="selectedBoard ? (selectedBoard.obstacles || []) : obstacles"
    />
  </template>
  
  <template v-else-if="(selectedBoard ? selectedBoard.type : boardType) === 'hexagonal'">
    <HexagonalBoard 
      :width="selectedBoard ? selectedBoard.size : boardSize" 
      :height="selectedBoard ? (selectedBoard.height || selectedBoard.size) : boardHeight"
      :cellColor="selectedBoard 
        ? (selectedBoard.transparentCells ? 'transparent' : selectedBoard.cellColor) 
        : (transparentCells ? 'transparent' : cellColor)"
      :obstacles="selectedBoard ? (selectedBoard.obstacles || []) : obstacles"
    />
  </template>
  
  <template v-else-if="(selectedBoard ? selectedBoard.type : boardType) === 'circular'">
    <CircularBoard 
      :width="selectedBoard ? selectedBoard.size : boardSize" 
      :cellColor="selectedBoard 
        ? (selectedBoard.transparentCells ? 'transparent' : selectedBoard.cellColor) 
        : (transparentCells ? 'transparent' : cellColor)"
      :obstacles="selectedBoard ? (selectedBoard.obstacles || []) : obstacles"
    />
  </template>
  
  <template v-else-if="(selectedBoard ? selectedBoard.type : boardType) === 'image'">
    <div class="board-image-only">
      <div v-if="!(selectedBoard ? selectedBoard.imageUrl : boardImagePreview)" class="no-image-placeholder">
        <p>Please upload a board image</p>
      </div>
      <!-- <div v-else class="board-image-display">
        <img :src="selectedBoard ? selectedBoard.imageUrl : boardImagePreview" alt="Board image" class="img-fluid" />
      </div> -->
    </div>
  </template>
</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// export default {
//   name: 'BuildBoardsPage',
// }

import BoardCrumbs from '@/components/BoardCrumbs.vue';
import GridBoard from './components/GridBoard.vue';
import HexagonalBoard from './components/HexagonalBoard.vue';
import CircularBoard from './components/CircularBoard.vue';
import { getAuth } from 'firebase/auth';
import { firestore, storage } from '@/firebase';
import { collection, addDoc, query, where, getDocs, Timestamp, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export default {
  name: 'BuildBoardsPage',
  components: {
    GridBoard,
    HexagonalBoard,
    CircularBoard,
    BoardCrumbs
  },
  data() {
    return {
      printLayout: '1',
      showPageIndicators: true,
      boardType: 'grid',
      boardSize: 8,
      boardHeight: 8,
      cellColor: '#e0e0e0',
      transparentCells: false,
      addRandomObstacles: false,
      obstacles: [],
      savedBoards: [],
      boardName: '',
      boardDescription: '',
      boardVersion: '1.0.0',
      boardImage: null,
      boardImagePreview: null,
      viewMode: 'list',
      editMode: false,
      viewOnlyMode: false,
      selectedBoard: null,
      isFullScreen: false,
      currentBoardId: null,
      currentUser: null,
      boardGameName: null,
      boardGameId: null,
      // Set when the editor is opened at /boards/:boardId/edit rather than
      // /build-boards/:gameId/new.
      routeBoardId: null,
      loadError: null,
      isEditor: false,
      isOwner: false,
       boardTheme: 'fantasy',
    selectedColorScheme: 'Primary',
    selectedPattern: 'None',
    colorSchemes: [
      {
        name: 'Primary',
        colors: ['#4285F4', '#34A853', '#FBBC05', '#EA4335']
      },
      {
        name: 'Earthy',
        colors: ['#8D6E63', '#A5D6A7', '#FFF59D', '#FFCC80']
      },
      {
        name: 'Jewel Tones',
        colors: ['#673AB7', '#3F51B5', '#00BCD4', '#009688']
      },
      {
        name: 'Pastels',
        colors: ['#BBDEFB', '#C8E6C9', '#FFF9C4', '#FFCCBC']
      },
      {
        name: 'Monochrome',
        colors: ['#212121', '#616161', '#9E9E9E', '#E0E0E0']
      }
    ],
    patterns: [
      {
        name: 'None',
        preview: '/img/patterns/none.png',
        url: ''
      },
      {
        name: 'Grid',
        preview: '/img/patterns/grid.png',
        url: 'url(/img/patterns/grid.png)'
      },
      {
        name: 'Dots',
        preview: '/img/patterns/dots.png',
        url: 'url(/img/patterns/dots.png)'
      },
      {
        name: 'Wood',
        preview: '/img/patterns/wood.png',
        url: 'url(/img/patterns/wood.png)'
      },
      {
        name: 'Marble',
        preview: '/img/patterns/marble.png',
        url: 'url(/img/patterns/marble.png)'
      }
    ],
    specialFeatures: {
      centerZone: false,
      shortcuts: false,
      resources: false,
      obstacles: false
    },
    }
  },
  computed: {
     // Calculate page indicator lines based on print layout
  pageIndicators() {
    const layout = parseInt(this.printLayout);
    if (layout === 1 || !this.showPageIndicators) {
      return []; // No indicators for single page
    }
    
    const indicators = [];
    
    if (layout === 4) {
      // 2x2 layout
      // Horizontal middle line
      indicators.push({
        type: 'horizontal',
        position: '50%'
      });
      
      // Vertical middle line
      indicators.push({
        type: 'vertical',
        position: '50%'
      });
    } 
    else if (layout === 6) {
      // 2x3 layout
      // Horizontal lines at 1/3 and 2/3
      indicators.push({
        type: 'horizontal',
        position: '33.333%'
      });
      
      indicators.push({
        type: 'horizontal',
        position: '66.666%'
      });
      
      // Vertical middle line
      indicators.push({
        type: 'vertical',
        position: '50%'
      });
    }
    
    return indicators;
  },
  
    boardContainerStyle() {
      const baseStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative'
      };
      
      if (this.isFullScreen) {
        return {
          ...baseStyle,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1050,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '2rem'
        };
      }
      
      return {
        ...baseStyle,
        minHeight: '500px',
        // The print stylesheet keys off this. It only ever existed on a
        // shadowed copy of this method, so multi-page print layouts never
        // actually took effect.
        'data-print-layout': this.printLayout
      };
    },
    boardBackgroundStyle() {
      // Use selected board's image URL if in view mode, otherwise use the preview
      const imageUrl = this.selectedBoard ? this.selectedBoard.imageUrl : this.boardImagePreview;
      
      if (imageUrl) {
        return {
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      }
      return {}
    }
  },
  async created() {
    const auth = getAuth();
    this.currentUser = auth.currentUser;
    
    // Check if view-only mode is specified in the query
    const viewOnly = this.$route.query.viewOnly === 'true';

    // This page is now the editor for ONE board and is reached two ways:
    //   /boards/:boardId/edit        — an existing board
    //   /build-boards/:gameId/new    — a blank one
    // The game id comes from the route when creating, and from the board
    // itself when editing, so an /boards/:id/edit link works on its own
    // without the caller having to know which game it belongs to.
    this.routeBoardId = this.$route.params.boardId || null;
    this.boardGameId = this.$route.params.boardGameId || null;

    if (this.routeBoardId) {
      const snap = await getDoc(doc(firestore, 'boards', this.routeBoardId));
      if (snap.exists()) {
        this.boardGameId = snap.data().boardGameId;
      } else {
        this.loadError = 'That board no longer exists.';
      }
    }

    // Load board game details and saved boards
    await this.loadBoardGame();
    await this.loadSavedBoards();

    // Open the board named in the URL, so /boards/:id/edit lands in its form
    // rather than on a blank one.
    if (this.routeBoardId) {
      const board = this.savedBoards.find((b) => b.id === this.routeBoardId);
      if (board) this.loadBoard(board);
    }

    // Check permissions
    if (this.currentUser) {
        // Check if user is owner or editor
        await this.checkUserPermissions();
      } else {
        // If user is not logged in, force view-only mode
        this.viewOnlyMode = true;
      }
      
      // If view-only is explicitly set in the URL, override permissions
      if (viewOnly) {
        this.viewOnlyMode = true;
      }
      
      // Now load the boards after setting viewOnlyMode
      this.loadSavedBoards();
    },
  
  beforeUnmount() {
    // Clean up event listeners
    if (this.isFullScreen) {
      document.removeEventListener('keydown', this.handleEscKey);
      document.body.style.overflow = '';
    }
  },
  methods: {
    async loadBoardGame() {
      try {
        console.log(this.boardGameId)
        const boardGameRef = doc(firestore, 'boardGames', this.boardGameId);
        const boardGameDoc = await getDoc(boardGameRef);
        
        if (boardGameDoc.exists()) {
          const boardGameData = boardGameDoc.data();
          this.boardGameName = boardGameData.name;
          this.boardGame = {
            id: boardGameDoc.id,
            ...boardGameData
          };
        } else {
          console.error('Board game not found');
        }
      } catch (error) {
        console.error('Error loading board game:', error);
      }
    },

    selectColorScheme(schemeName) {
    this.selectedColorScheme = schemeName;
    
    // Apply the first color of the selected scheme to the cell color
    const scheme = this.colorSchemes.find(s => s.name === schemeName);
    if (scheme && scheme.colors.length > 0) {
      this.cellColor = scheme.colors[0];
    }
  },
  
  selectPattern(patternName) {
    this.selectedPattern = patternName;
  },
  
  applyThemeDefaults() {
    // Set default colors and patterns based on selected theme
    switch(this.boardTheme) {
      case 'fantasy':
        this.selectColorScheme('Jewel Tones');
        this.selectPattern('Marble');
        break;
      case 'scifi':
        this.selectColorScheme('Primary');
        this.selectPattern('Grid');
        break;
      case 'medieval':
        this.selectColorScheme('Earthy');
        this.selectPattern('Wood');
        break;
      case 'modern':
        this.selectColorScheme('Monochrome');
        this.selectPattern('None');
        break;
      case 'abstract':
        this.selectColorScheme('Pastels');
        this.selectPattern('Dots');
        break;
      case 'natural':
        this.selectColorScheme('Earthy');
        this.selectPattern('None');
        break;
      default:
        // Default or custom - do nothing
        break;
    }
  },
  
    // NOTE: a block of duplicate generateBoard/saveBoard/loadBoard/resetForm
    // definitions used to sit here, pasted in with their placeholder comments
    // ("// ... existing properties") still in them. They were shadowed by the
    // real definitions below, so they never ran — and the theme, colour
    // scheme, pattern, special-feature and print-layout handling they were
    // meant to add was silently missing. That handling is now merged into the
    // real methods; the dead copies are gone.
    generateBoard() {
      if (this.addRandomObstacles) {
        this.createRandomObstacles();
      } else {
        this.obstacles = [];
      }
    },
    createRandomObstacles() {
      const totalCells = this.boardType === 'circular' 
        ? this.boardSize * this.boardSize 
        : this.boardSize * this.boardHeight;
      
      const obstacleCount = Math.floor(totalCells * 0.1); // 10% obstacles
      this.obstacles = Array.from(
        {length: obstacleCount}, 
        () => ({
          x: Math.floor(Math.random() * this.boardSize),
          y: Math.floor(Math.random() * (this.boardType === 'circular' ? this.boardSize : this.boardHeight))
        })
      );
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      this.boardImage = file;
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = e => {
        this.boardImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    formatDate(timestamp) {
      if (!timestamp) return '';
      
      // If it's a Firestore Timestamp, convert to JS Date
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    },
    
    async uploadBoardImage() {
      if (!this.boardImage) return null;
      
      try {
        // Create a unique filename
        const filename = `boards/${this.currentUser.uid}/${Date.now()}-${this.boardImage.name}`;
        const storageRef = ref(storage, filename);
        
        // Upload the file
        await uploadBytes(storageRef, this.boardImage);
        
        // Get the download URL
        const downloadURL = await getDownloadURL(storageRef);
        return downloadURL;
      } catch (error) {
        console.error('Error uploading image:', error);
        return null;
      }
    },
    
    async saveBoard() {
      if (!this.currentUser) {
        alert('Please log in to save boards');
        return;
      }

      if (!this.boardName) {
        alert('Please provide a board name');
        return;
      }
      
      if (!this.boardVersion) {
        alert('Please provide a board version');
        return;
      }

      try {
        // Upload image if one is selected
        let imageUrl = this.boardImagePreview;
        if (this.boardImage) {
          imageUrl = await this.uploadBoardImage();
        }
        
        const boardData = {
          userId: this.currentUser.uid,
          boardGameId: this.boardGameId,
          name: this.boardName,
          description: this.boardDescription || '',
          version: this.boardVersion,
          type: this.boardType,
          size: this.boardSize,
          height: this.boardHeight,
          cellColor: this.transparentCells ? 'transparent' : this.cellColor,
          transparentCells: this.transparentCells,
          obstacles: this.obstacles,
          imageUrl: imageUrl,
          // These are collected by the form and were never written, so every
          // theme and print-layout control on this page did nothing.
          theme: this.boardTheme,
          colorScheme: this.selectedColorScheme,
          pattern: this.selectedPattern,
          specialFeatures: { ...this.specialFeatures },
          printLayout: this.printLayout,
          updatedAt: Timestamp.now()
        };

        // If not in edit mode, add createdAt timestamp
        if (!this.editMode) {
          boardData.createdAt = Timestamp.now();
        }

        const boardsRef = collection(firestore, 'boards');
        
        if (this.editMode && this.currentBoardId) {
          // Update existing board
          const boardDocRef = doc(firestore, 'boards', this.currentBoardId);
          await updateDoc(boardDocRef, boardData);
          alert(`Board "${this.boardName}" v${this.boardVersion} updated successfully!`);
          
          // Exit edit mode
          this.editMode = false;
          this.currentBoardId = null;
        } else {
          // Create new board
          await addDoc(boardsRef, boardData);
          alert(`Board "${this.boardName}" v${this.boardVersion} saved successfully!`);
        }
        
        // Reload saved boards
        await this.loadSavedBoards();

        // Reset form
        this.resetForm();
      } catch (error) {
        console.error('Error saving board:', error);
        alert(`Failed to ${this.editMode ? 'update' : 'save'} board`);
      }
    },
    async checkUserPermissions() {
      if (!this.currentUser || !this.boardGameId) {
        this.viewOnlyMode = true;
        return;
      }

      try {
        // Check if user is the owner of the board game
        const boardGameRef = doc(firestore, 'boardGames', this.boardGameId);
        const boardGameSnap = await getDoc(boardGameRef);
        
        if (boardGameSnap.exists()) {
          const boardGameData = boardGameSnap.data();
          this.isOwner = boardGameData.userId === this.currentUser.uid;
          
          if (this.isOwner) {
            this.viewOnlyMode = false;
            return;
          }
        }
        
        // Check if user is an editor
        const editorsRef = collection(firestore, 'boardGameEditors');
        const q = query(
          editorsRef,
          where('boardGameId', '==', this.boardGameId),
          where('email', '==', this.currentUser.email),
          where('status', '==', 'accepted')
        );
        
        const querySnapshot = await getDocs(q);
        this.isEditor = !querySnapshot.empty;
        
        // Set view-only mode if user is not an owner or editor
        this.viewOnlyMode = !(this.isOwner || this.isEditor);
      } catch (error) {
        console.error('Error checking permissions:', error);
        this.viewOnlyMode = true;
      }
    },
    
    async loadSavedBoards() {
      if (!this.boardGameId) {
        console.log('No boardGameId provided, cannot load boards');
        return;
      }
      
      try {
        const boardsRef = collection(firestore, 'boards');
        const q = query(
          boardsRef,
          where('boardGameId', '==', this.boardGameId),
          where('userId', '==', this.currentUser.uid)
        );
        
        const querySnapshot = await getDocs(q);
        this.savedBoards = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error loading boards:', error);
        this.savedBoards = [];
      }

      try {
        console.log('Loading boards for game ID:', this.boardGameId);
        console.log('View-only mode:', this.viewOnlyMode);
        console.log('Current user:', this.currentUser ? this.currentUser.uid : 'not logged in');
        
        const boardsRef = collection(firestore, 'boards');
        let q;
        
        if (this.currentUser && !this.viewOnlyMode) {
          // If user is logged in and not in view-only mode, show only their boards
          console.log('Loading only user boards');
          q = query(
            boardsRef, 
            where('userId', '==', this.currentUser.uid),
            where('boardGameId', '==', this.boardGameId)
          );
        } else {
          // In view-only mode or for non-logged in users, show all boards for this game
          console.log('Loading all boards for the game');
          q = query(
            boardsRef, 
            where('boardGameId', '==', this.boardGameId)
          );
        }

        const querySnapshot = await getDocs(q);
        console.log('Found boards:', querySnapshot.size);
        
        this.savedBoards = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Saved boards:', this.savedBoards.length);
      } catch (error) {
        console.error('Error loading boards:', error);
        alert('Failed to load saved boards');
      }
    },
    
    viewBoard(board) {
      this.selectedBoard = board;
      // Scroll to the top to show the board preview
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    closeSelectedBoard() {
      // Exit full screen mode if active
      if (this.isFullScreen) {
        this.isFullScreen = false;
      }
      this.selectedBoard = null;
    },
    
    toggleFullScreen() {
      this.isFullScreen = !this.isFullScreen;
      
      if (this.isFullScreen) {
        // Add event listener for ESC key to exit full screen
        document.addEventListener('keydown', this.handleEscKey);
        // Prevent scrolling when in full screen
        document.body.style.overflow = 'hidden';
      } else {
        // Remove event listener when exiting full screen
        document.removeEventListener('keydown', this.handleEscKey);
        // Restore scrolling
        document.body.style.overflow = '';
      }
    },
    
    handleEscKey(event) {
      if (event.key === 'Escape' && this.isFullScreen) {
        this.toggleFullScreen();
      }
    },
    loadBoard(board) {
      if (this.viewOnlyMode) {
        // In view-only mode, just display the board
        this.viewBoard(board);
        return;
      }
      
      // Load the selected board's configuration for editing
      this.boardType = board.type;
      this.boardSize = board.size;
      this.boardHeight = board.height || board.size;
      this.cellColor = board.cellColor === 'transparent' ? '#e0e0e0' : board.cellColor;
      this.transparentCells = board.transparentCells || board.cellColor === 'transparent';
      this.obstacles = board.obstacles || [];
      this.boardName = board.name;
      this.boardDescription = board.description || '';
      this.boardVersion = board.version || '1.0.0';
      this.boardImagePreview = board.imageUrl || null;
      this.boardImage = null; // Reset the file input
      this.boardTheme = board.theme || 'fantasy';
      this.selectedColorScheme = board.colorScheme || 'Primary';
      this.selectedPattern = board.pattern || 'None';
      this.specialFeatures = board.specialFeatures || {
        centerZone: false,
        shortcuts: false,
        resources: false,
        obstacles: false
      };
      this.printLayout = board.printLayout || '1';
      
      // Set edit mode
      this.editMode = true;
      this.currentBoardId = board.id;
      
      // Scroll to the top to show the edit form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    cancelEdit() {
      // Reset form and exit edit mode
      this.editMode = false;
      this.currentBoardId = null;
      this.resetForm();
    },
    
    resetForm() {
      // Reset all form fields to default values
      this.boardType = 'grid';
      this.boardSize = 8;
      this.boardHeight = 8;
      this.cellColor = '#e0e0e0';
      this.transparentCells = false;
      this.addRandomObstacles = false;
      this.obstacles = [];
      this.boardName = '';
      this.boardDescription = '';
      this.boardVersion = '1.0.0';
      this.boardImage = null;
      this.boardImagePreview = null;
      this.boardTheme = 'fantasy';
      this.selectedColorScheme = 'Primary';
      this.selectedPattern = 'None';
      this.specialFeatures = {
        centerZone: false,
        shortcuts: false,
        resources: false,
        obstacles: false
      };
      this.printLayout = '1';
    }
  }
}
</script>

<style scoped>
.card {
  margin-bottom: 20px;
}

.board-image-container {
  height: 180px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.board-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.board-placeholder {
  height: 180px;
  background-color: var(--bg-sketch, #f8f9fa);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-muted, #6c757d);
  font-size: 1.2rem;
}

.placeholder-text {
  text-align: center;
  padding: 1rem;
}

.board-image-only {
  width: 100%;
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-image-placeholder {
  padding: 2rem;
  background-color: var(--bg-sketch, rgba(0, 0, 0, 0.05));
  border-radius: 8px;
  text-align: center;
}

/* Full screen mode styles */
.board-container.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1050;
  background-color: var(--bg-white, rgba(255, 255, 255, 0.95));
  padding: 2rem;
}

/* Increase board size in full screen mode */
.board-container.fullscreen .grid-board,
.board-container.fullscreen .hexagonal-board,
.board-container.fullscreen .circular-board,
.board-container.fullscreen .board-image-display img {
  transform: scale(1.5);
  transition: transform 0.3s ease;
}

.board-image-display {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.board-image-display img {
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/*Theme*/

/* Color scheme picker styles */
.color-scheme-option {
  width: 80px;
  margin-right: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  border-radius: 6px;
  padding: 5px;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.color-scheme-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.color-scheme-option.active {
  border-color: var(--pop-orange, #007bff);
  box-shadow: 0 2px 8px var(--shadow-med, rgba(0,0,0,0.15));
}

.color-preview {
  height: 20px;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 5px;
}

.color-sample {
  flex: 1;
  height: 20px;
}

.color-scheme-name {
  font-size: 0.8rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Pattern picker styles */
.pattern-option {
  width: 80px;
  margin-right: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  border-radius: 6px;
  padding: 5px;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.pattern-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.pattern-option.active {
  border-color: var(--pop-orange, #007bff);
  box-shadow: 0 2px 8px var(--shadow-med, rgba(0,0,0,0.15));
}

.pattern-preview {
  height: 40px;
  width: 100%;
  border-radius: 4px;
  background-size: cover;
  background-position: center;
  margin-bottom: 5px;
  border: 1px solid var(--pencil-light, #dee2e6);
}

.pattern-name {
  font-size: 0.8rem;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Apply background pattern to board container when a pattern is selected */
.board-container {
  background-repeat: repeat;
  background-size: auto;
  transition: background 0.3s ease;
}

/* Gap utility class for flexbox containers */
.gap-2 {
  gap: 0.5rem;
}

/* Page indicator styles */
/* Page indicator styles */
.page-indicator {
  position: absolute;
  pointer-events: none;
  z-index: 10;
}

.page-indicator.horizontal {
  width: 100%;
  height: 0;
  border-top: 2px dashed rgba(0, 0, 0, 0.3);
  left: 0;
}

.page-indicator.vertical {
  height: 100%;
  width: 0;
  border-left: 2px dashed rgba(0, 0, 0, 0.3);
  top: 0;
}

/* Page number indicators */
.page-number {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.8);
  border: 1px dotted rgba(0, 0, 0, 0.4);
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  z-index: 11;
  pointer-events: none;
}

/* 2x2 layout (4 pages) - Properly position page numbers in each quadrant */
.board-container[data-print-layout="4"] .page-number.page-1 {
  top: 10px;
  left: 10px;
}

.board-container[data-print-layout="4"] .page-number.page-2 {
  top: 10px;
  right: 10px;
}

.board-container[data-print-layout="4"] .page-number.page-3 {
  bottom: 10px;
  left: 10px;
}

.board-container[data-print-layout="4"] .page-number.page-4 {
  bottom: 10px;
  right: 10px;
}

/* 2x3 layout (6 pages) - Properly position page numbers in each section */
.board-container[data-print-layout="6"] .page-number.page-1 {
  top: 10px;
  left: 10px;
}

.board-container[data-print-layout="6"] .page-number.page-2 {
  top: 10px;
  right: 10px;
}

.board-container[data-print-layout="6"] .page-number.page-3 {
  top: calc(33.333% + 5px);
  left: 10px;
}

.board-container[data-print-layout="6"] .page-number.page-4 {
  top: calc(33.333% + 5px);
  right: 10px;
}

.board-container[data-print-layout="6"] .page-number.page-5 {
  top: calc(66.666% + 5px);
  left: 10px;
}

.board-container[data-print-layout="6"] .page-number.page-6 {
  top: calc(66.666% + 5px);
  right: 10px;
}


/* Print button styling */
.print-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  z-index: 20;
  background-color: var(--bg-white, #fff);
  border: 1px solid var(--pencil-light, #ddd);
  border-radius: 4px;
  padding: 5px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: var(--ink, inherit);
  box-shadow: 0 2px 4px var(--shadow-soft, rgba(0,0,0,0.1));
}

.print-button:hover {
  background-color: var(--bg-elevated, #f8f9fa);
}

.print-button i {
  font-size: 16px;
}
</style>